import { supabase } from '../lib/supabase';

const EDGE_FUNCTION_NAME = 'duffel-proxy';

async function callDuffel({ endpoint, method = 'GET', payload = null }) {
  const { data, error } = await supabase?.functions?.invoke(EDGE_FUNCTION_NAME, {
    body: { endpoint, method, payload },
  });

  if (error) {
    throw new Error(error?.message || 'Edge function invocation failed');
  }

  if (data?.error) {
    throw new Error(data?.error);
  }

  return data;
}

export async function searchFlights({ origin, destination, departureDate, returnDate, passengers, cabinClass }) {
  const slices = [
    {
      origin,
      destination,
      departure_date: departureDate,
    },
  ];

  if (returnDate) {
    slices?.push({
      origin: destination,
      destination: origin,
      departure_date: returnDate,
    });
  }

  const passengerList = [];
  for (let i = 0; i < (passengers?.adults || 1); i++) {
    passengerList?.push({ type: 'adult' });
  }
  for (let i = 0; i < (passengers?.children || 0); i++) {
    passengerList?.push({ type: 'child' });
  }
  for (let i = 0; i < (passengers?.infants || 0); i++) {
    passengerList?.push({ type: 'infant_without_seat' });
  }

  const payload = {
    data: {
      slices,
      passengers: passengerList,
      cabin_class: cabinClass || 'economy',
    },
  };

  const result = await callDuffel({
    endpoint: '/air/offer_requests?return_offers=true',
    method: 'POST',
    payload,
  });

  return result?.data;
}

export async function getOffers(offerRequestId) {
  const result = await callDuffel({
    endpoint: `/air/offers?offer_request_id=${offerRequestId}&limit=50`,
    method: 'GET',
  });
  return result?.data || [];
}

export async function getOffer(offerId) {
  const result = await callDuffel({
    endpoint: `/air/offers/${offerId}`,
    method: 'GET',
  });
  return result?.data;
}

export function normalizeOffer(offer) {
  const firstSlice = offer?.slices?.[0];
  const lastSlice = offer?.slices?.[offer?.slices?.length - 1];
  const firstSegment = firstSlice?.segments?.[0];
  const lastSegment = lastSlice?.segments?.[lastSlice?.segments?.length - 1];

  return {
    id: offer?.id,
    totalAmount: offer?.total_amount,
    totalCurrency: offer?.total_currency,
    baseAmount: offer?.base_amount,
    baseCurrency: offer?.base_currency,
    taxAmount: offer?.tax_amount,
    taxCurrency: offer?.tax_currency,
    airline: {
      name: firstSegment?.marketing_carrier?.name || firstSegment?.operating_carrier?.name,
      iataCode: firstSegment?.marketing_carrier?.iata_code || firstSegment?.operating_carrier?.iata_code,
      logoUrl: firstSegment?.marketing_carrier?.logo_symbol_url,
    },
    slices: offer?.slices?.map((slice) => ({
      id: slice?.id,
      origin: slice?.origin?.iata_code,
      originName: slice?.origin?.name,
      originCity: slice?.origin?.city?.name,
      destination: slice?.destination?.iata_code,
      destinationName: slice?.destination?.name,
      destinationCity: slice?.destination?.city?.name,
      departureAt: slice?.segments?.[0]?.departing_at,
      arrivalAt: slice?.segments?.[slice?.segments?.length - 1]?.arriving_at,
      duration: slice?.duration,
      stops: slice?.segments?.length - 1,
      segments: slice?.segments?.map((seg) => ({
        id: seg?.id,
        origin: seg?.origin?.iata_code,
        destination: seg?.destination?.iata_code,
        departureAt: seg?.departing_at,
        arrivalAt: seg?.arriving_at,
        duration: seg?.duration,
        flightNumber: `${seg?.marketing_carrier?.iata_code}${seg?.marketing_carrier_flight_number}`,
        aircraft: seg?.aircraft?.name,
        carrier: seg?.marketing_carrier?.name,
      })),
    })),
    conditions: {
      refundable: offer?.conditions?.refund_before_departure?.allowed,
      changeable: offer?.conditions?.change_before_departure?.allowed,
      refundPenalty: offer?.conditions?.refund_before_departure?.penalty_amount,
      changePenalty: offer?.conditions?.change_before_departure?.penalty_amount,
    },
    expiresAt: offer?.expires_at,
    passengersCount: offer?.passengers?.length,
  };
}
